/**
 * Split-tender settlement (POS-I32). A cashier may tender more cash than the cash share
 * (a bigger note) while the rest is card / wallet / other. The surplus is change handed back
 * in cash. This mirrors the backend ledger allocation (`netPaymentsAfterChange`: change comes
 * out of the cash drawer first), so the UI only accepts what the backend can post.
 */
export interface SplitTenderRow {
  method: string;
  amount: string | number;
}

export type SplitTenderStatus = 'short' | 'exact' | 'change' | 'noncash_excess';

export interface SplitTenderResult {
  status: SplitTenderStatus;
  /** Sum of every tender row. */
  tendered: number;
  /** Sum of the CASH rows only. */
  cashTendered: number;
  /** payable - tendered when tendered < payable, else 0. */
  remaining: number;
  /** Change to hand back in cash (only when status is 'change'). */
  change: number;
  /** Amount by which non-cash tenders alone exceed payable (only when status is 'noncash_excess'). */
  excess: number;
}

const r2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

export function evaluateSplitTender(rows: readonly SplitTenderRow[], payable: number): SplitTenderResult {
  const due = r2(payable);
  const amounts = rows.map((r) => r2(Number(r.amount || 0)));
  const tendered = r2(amounts.reduce((s, a) => s + a, 0));
  const cashTendered = r2(rows.reduce((s, r, i) => (r.method === 'CASH' ? s + amounts[i] : s), 0));
  const diff = r2(tendered - due);
  if (Math.abs(diff) < 0.01) return { status: 'exact', tendered, cashTendered, remaining: 0, change: 0, excess: 0 };
  if (diff < 0) return { status: 'short', tendered, cashTendered, remaining: r2(-diff), change: 0, excess: 0 };
  // Surplus: allowed only when cash alone can give it back.
  if (diff <= cashTendered + 0.001) return { status: 'change', tendered, cashTendered, remaining: 0, change: diff, excess: 0 };
  return { status: 'noncash_excess', tendered, cashTendered, remaining: 0, change: 0, excess: r2(diff - cashTendered) };
}
